import React, { useState, useEffect } from 'react';
import { db, functions } from '../firebaseConfig';
import { collection, query, where, onSnapshot, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const Dashboard = ({ user }) => {
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPickerApiLoaded, setIsPickerApiLoaded] = useState(false);

  // State per i modali
  const [createModal, setCreateModal] = useState({ isOpen: false, step: 1, sheetId: null, sheetName: '', shortName: '' });
  const [editModal, setEditModal] = useState({ isOpen: false, list: null, newShortName: '', newType: '' });
  const [notification, setNotification] = useState({ isOpen: false, message: '' });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'todoLists'), where('ownerId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const userLists = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLists(userLists);
    });
    return () => unsubscribe();
  }, [user]);

  const resetCreateModal = () => {
    setCreateModal({ isOpen: false, step: 1, sheetId: null, sheetName: '', shortName: '' });
    setFormError('');
  };

  const pickerCallback = (data) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const file = data.docs[0];
      setCreateModal({ ...createModal, isOpen: true, sheetId: file.id, sheetName: file.name });
    }
  };

  const handleShortNameSubmit = () => {
    if (!createModal.shortName || !/^[a-z0-9-]+$/.test(createModal.shortName)) {
      setFormError("Nome non valido. Usa solo lettere minuscole, numeri e trattini.");
      return;
    }
    setFormError('');
    setCreateModal({ ...createModal, step: 2 });
  };

  const handleCreateList = async (type) => {
    setIsLoading(true);
    try {
      const createListFunc = httpsCallable(functions, 'createTodoList');
      await createListFunc({ ...createModal, type });
      setNotification({ isOpen: true, message: `Lista "${createModal.sheetName}" creata con successo!` });
    } catch (error) {
      console.error("Errore creazione lista:", error);
      setNotification({ isOpen: true, message: `Errore: ${error.message}` });
    } finally {
      setIsLoading(false);
      resetCreateModal();
    }
  };

  const handleUpdateList = async () => {
    if (!editModal.newShortName || !/^[a-z0-9-]+$/.test(editModal.newShortName)) {
        setFormError("Il nuovo nome breve non è valido.");
        return;
    }
    setIsLoading(true);
    setFormError('');
    try {
        const listRef = doc(db, 'todoLists', editModal.list.id);
        await updateDoc(listRef, {
            shortName: editModal.newShortName,
            type: editModal.newType
        });
        setNotification({ isOpen: true, message: "Lista aggiornata con successo!" });
    } catch (error) {
        console.error("Errore aggiornamento lista:", error);
        setNotification({ isOpen: true, message: `Errore: ${error.message}` });
    } finally {
        setIsLoading(false);
        setEditModal({ isOpen: false, list: null, newShortName: '', newType: '' });
    }
  };

  const openGooglePicker = () => {
    const token = sessionStorage.getItem('googleAccessToken');
    if (!token) {
      setNotification({ isOpen: true, message: 'Token di accesso Google mancante. Prova a fare il logout e login.' });
      return;
    }
    const picker = new window.google.picker.PickerBuilder()
      .addView(window.google.picker.ViewId.SPREADSHEETS)
      .setOAuthToken(token)
      .setDeveloperKey(GOOGLE_API_KEY)
      .setCallback(pickerCallback)
      .build();
    picker.setVisible(true);
  };
  
  const handleCopyUrl = (shortName) => {
    const url = `${window.location.origin}/l/${shortName}`;
    navigator.clipboard.writeText(url).then(() => {
        setNotification({ isOpen: true, message: 'URL copiato!' });
    });
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => window.gapi.load('picker', () => setIsPickerApiLoaded(true));
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  const deleteList = async (listId) => {
    if (window.confirm("Sei sicuro di voler eliminare questa lista?")) {
      await deleteDoc(doc(db, 'todoLists', listId));
    }
  }

  const renderModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        {createModal.isOpen && createModal.step === 1 && (
          <>
            <h2 className="text-xl font-bold mb-4">Crea la tua lista</h2>
            <label htmlFor="shortName" className="block text-sm font-medium text-gray-700">Nome breve per l'URL</label>
            <input type="text" id="shortName" value={createModal.shortName} onChange={(e) => setCreateModal({ ...createModal, shortName: e.target.value })} placeholder="es. spesa-settimanale" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"/>
            {formError && <p className="text-red-500 text-sm mt-1">{formError}</p>}
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={resetCreateModal} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Annulla</button>
              <button onClick={handleShortNameSubmit} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Avanti</button>
            </div>
          </>
        )}
        {createModal.isOpen && createModal.step === 2 && (
          <>
            <h2 className="text-xl font-bold mb-4">Scegli il tipo di lista</h2>
            <p className="text-gray-600 mb-6">Scegliendo <strong>Condivisa</strong>, l'app verrà aggiunta come editor al tuo file per permettere a tutti di vedere le modifiche.</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setCreateModal({...createModal, step: 1})} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Indietro</button>
              <button disabled="true" onClick={() => handleCreateList('shared')} className="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg">Condivisa</button>
              <button onClick={() => handleCreateList('individual')} className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg">Locale</button>
            </div>
          </>
        )}
        {editModal.isOpen && (
             <>
                <h2 className="text-xl font-bold mb-4">Modifica Lista</h2>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="editShortName" className="block text-sm font-medium text-gray-700">Nome breve per l'URL</label>
                        <input type="text" id="editShortName" value={editModal.newShortName} onChange={(e) => setEditModal({ ...editModal, newShortName: e.target.value })} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"/>
                    </div>
                    <div>
                        <label htmlFor="editType" className="block text-sm font-medium text-gray-700">Tipo di Lista</label>
                        <select id="editType" value={editModal.newType} onChange={(e) => setEditModal({ ...editModal, newType: e.target.value })} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                            <option disabled="true" value="shared">Condivisa</option>
                            <option value="individual">Locale</option>
                        </select>
                    </div>
                </div>
                {formError && <p className="text-red-500 text-sm mt-2">{formError}</p>}
                <div className="mt-6 flex justify-end space-x-3">
                    <button onClick={() => setEditModal({ isOpen: false, list: null, newShortName: '', newType: '' })} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Annulla</button>
                    <button onClick={handleUpdateList} disabled={isLoading} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-gray-400">{isLoading ? 'Salvataggio...' : 'Salva'}</button>
                </div>
             </>
        )}
      </div>
    </div>
  );

  const renderNotification = () => (
    <div className="fixed bottom-5 right-5 bg-gray-800 text-white py-2 px-4 rounded-lg shadow-lg animate-pulse">
        <p>{notification.message}</p>
    </div>
  );

  if (notification.isOpen) {
    setTimeout(() => setNotification({ isOpen: false, message: '' }), 3000);
  }

  return (
    <div>
      {(createModal.isOpen || editModal.isOpen) && renderModal()}
      {notification.isOpen && renderNotification()}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Le tue Liste</h1>
        <button onClick={openGooglePicker} disabled={isLoading || !isPickerApiLoaded} className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-gray-400 disabled:cursor-wait">
          {isLoading ? 'Elaborazione...' : !isPickerApiLoaded ? 'Caricamento API...' : '+ Crea Nuova Lista'}
        </button>
      </div>
      <div className="bg-white p-4 rounded-lg shadow-md">
        {lists.length > 0 ? (
          <ul>
            {lists.map(list => (
              <li key={list.id} className="flex justify-between items-center p-3 border-b last:border-b-0 group">
                <div>
                  <a href={`/l/${list.shortName}`} className="text-blue-600 hover:underline font-semibold">{list.sheetName || list.shortName}</a>
                  <p onClick={() => handleCopyUrl(list.shortName)} className="text-sm text-gray-500 cursor-pointer hover:text-blue-500" title="Copia URL">/l/{list.shortName}</p>
                </div>
                <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setEditModal({ isOpen: true, list: list, newShortName: list.shortName, newType: list.type })} className="p-2 hover:bg-gray-200 rounded-full" title="Modifica">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                    </button>
                    <button onClick={() => deleteList(list.id)} className="p-2 hover:bg-gray-200 rounded-full" title="Elimina">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                    </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>Non hai ancora creato nessuna lista.</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;